import { Badge, IconButton, Menu, Typography, Chip, Divider, List, ListItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from "@mui/material";
import { Notifications, Close, CheckCircle, Cancel } from "@mui/icons-material";
import React, { useState, useEffect } from "react";
import AddAlertMessage from "../alert/Alert";
import { ERROR, SUCCESS, SOMETHING_WENT_WRONG, NOTIFICATION_REJECTED, NOTIFICATION_PROCESSED } from "../../utils/constants";
import { useStyles } from "./style";
import { fetchPendingNotifications, rejectNotification, fetchSourceData } from "./notificationService";

export const REGISTER_CONFIG = {
  OUTPATIENT: {
    label: "बहिरङ्ग सेवा",
    apiKey: "outPatient",
  }
};

const getTimeAgo = (createdAt) => {
  if (!createdAt) return "";
  const created = new Date(createdAt);
  const now = new Date();
  const diffMs = now - created;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "भर्खरै";
  if (diffMins < 60) return `${diffMins} मिनेट अगाडि`;
  if (diffHours < 24) return `${diffHours} घण्टा अगाडि`;
  if (diffDays < 30) return `${diffDays} दिन अगाडि`;
  return created.toLocaleDateString("ne-NP");
};

const RegisterNotificationBell = ({ targetRegister, onAccept, onReject }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [rejectDialog, setRejectDialog] = useState({ open: false, notification: null, reason: "" });
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const isOpen = Boolean(anchorEl);

  const updateCount = async () => {
    try {
      const result = await fetchPendingNotifications(targetRegister);
      setCount(result.totalSize || 0);
    } catch (e) { }
  };

  useEffect(() => { updateCount(); }, [targetRegister]);

  useEffect(() => {
    if (isOpen && notifications.length === 0) {
      fetchPendingNotifications(targetRegister)
        .then((result) => {
          setNotifications(result.objectList || []);
          setCount(result.totalSize || 0);
        })
        .catch(() => { });
    }
  }, [isOpen, targetRegister]);

  const handleAccept = async (notification) => {
    try {
      const config = REGISTER_CONFIG[notification.sourceRegister];
      if (!config) {
        AddAlertMessage({ type: ERROR, message: `Source register ${notification.sourceRegister} not supported` });
        return;
      }

      const sourceData = await fetchSourceData(config.apiKey, notification.sourceId);

      if (onAccept) {
        onAccept(notification, sourceData);
      }

      setAnchorEl(null);
      AddAlertMessage({ type: SUCCESS, message: NOTIFICATION_PROCESSED });
      updateCount();
    } catch (error) {
      AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.notification || !rejectDialog.reason.trim()) {
      AddAlertMessage({ type: ERROR, message: "Please provide rejection reason" });
      return;
    }
    try {
      await rejectNotification({
        notificationId: rejectDialog.notification.id,
        rejectionReason: rejectDialog.reason.trim(),
      });

      if (onReject) onReject(rejectDialog.notification);

      setRejectDialog({ open: false, notification: null, reason: "" });
      setAnchorEl(null);
      AddAlertMessage({ type: SUCCESS, message: NOTIFICATION_REJECTED });
      updateCount();
    } catch (error) {
      AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
    }
  };

  const grouped = notifications.reduce((acc, n) => {
    const reg = n.sourceRegister || "OTHER";
    (acc[reg] || (acc[reg] = [])).push(n);
    return acc;
  }, {});

  return (
    <>
      <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <Badge badgeContent={count} color="error">
          <Notifications />
        </Badge>
      </IconButton>

      <Menu anchorEl={anchorEl} open={isOpen} onClose={() => setAnchorEl(null)} PaperProps={{ className: classes.menuPaper }} transformOrigin={{ horizontal: "right", vertical: "top" }} anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
        <div className={classes.header}>
          <Typography variant="subtitle1" className={classes.title}>पेन्डिङ सूचनाहरू</Typography>
          <IconButton className={classes.closeButton} onClick={() => setAnchorEl(null)}><Close fontSize="small" /></IconButton>
        </div>
        <Divider />
        {!notifications.length ? (
          <div className={classes.emptyState}>
            <Notifications className={classes.notificationIcon} />
            <Typography variant="body2" color="text.secondary">No pending notifications</Typography>
          </div>
        ) : (
          Object.entries(grouped).map(([register, notifs]) => (
            <div key={register} className={classes.groupContainer}>
              <div className={classes.groupHeader}>
                <Typography className={classes.groupTitle}>{REGISTER_CONFIG[register]?.label || register}</Typography>
                <Chip label={notifs.length} size="small" color="primary" className={classes.chip} />
              </div>
              <List dense className={classes.listContainer}>
                {notifs.map((n) => (
                  <ListItem key={n.id} className={classes.listItem} secondaryAction={
                    <div className={classes.actionButtons}>
                      <IconButton size="small" color="success" onClick={() => handleAccept(n)}>
                        <CheckCircle fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setRejectDialog({ open: true, notification: n, reason: "" })}>
                        <Cancel fontSize="small" />
                      </IconButton>
                    </div>
                  }>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight={600}>{`${n.patientFirstName || ""} ${n.patientLastName || ""}`.trim() || ""}</Typography>}
                      secondary={
                        <div className={classes.secondaryText}>
                          <Typography variant="caption" color="text.secondary">मूल दर्ता नं: {n.muldartaaNumber || ""}</Typography>
                          <Typography variant="caption" color="text.disabled">{getTimeAgo(n.createdAt)}</Typography>
                        </div>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </div>
          ))
        )}
      </Menu>

      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, notification: null, reason: "" })} maxWidth="sm" fullWidth>
        <DialogTitle className={classes.dialogTitle}>अस्वीकारको कारण</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <TextField fullWidth value={rejectDialog.reason} onChange={(e) => setRejectDialog({ open: true, notification: rejectDialog.notification, reason: e.target.value })} placeholder="अस्वीकारको कारण लेख्नुहोस्..." />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setRejectDialog({ open: false, notification: null, reason: "" })} color="inherit">रद्द</Button>
          <Button onClick={handleReject} color="error" variant="contained" disabled={!rejectDialog.reason.trim()}>अस्वीकार</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RegisterNotificationBell;
